import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Clock } from "lucide-react";
import { fetchArticle, fetchArticles } from "@/lib/api";
import ArticleCard from "@/components/ArticleCard";

export default function ArticleDetail() {
  const { id } = useParams();
  const [a, setA] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetchArticle(id)
      .then((art) => {
        setA(art);
        return fetchArticles({ category: art.category }).then((list) =>
          setRelated(list.filter((x) => x.id !== art.id).slice(0, 3))
        );
      })
      .catch(() => setA(null))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="max-w-3xl mx-auto px-6 py-32 font-serif italic text-xl text-sand-700">Loading…</div>;
  if (!a) return (
    <div className="max-w-3xl mx-auto px-6 py-32 text-center">
      <p className="font-serif italic text-2xl text-sand-700">Story not found.</p>
      <Link to="/articles" className="mt-6 inline-block font-mono text-xs tracking-widest uppercase text-brand-500">← All stories</Link>
    </div>
  );

  return (
    <div data-testid="article-detail-page" className="bg-sand-50">
      <div className="max-w-3xl mx-auto px-6 md:px-12 pt-10">
        <Link to="/articles" data-testid="back-to-articles" className="inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.25em] uppercase text-sand-700 hover:text-brand-500 transition-colors">
          <ArrowLeft size={13} /> Back to Editorial
        </Link>
      </div>

      <article className="max-w-3xl mx-auto px-6 md:px-12 pt-10 pb-20">
        <div className="font-mono text-[11px] tracking-[0.3em] uppercase text-brand-500 mb-6">
          {a.category}
        </div>
        <h1 className="font-serif font-black text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-sand-900">
          {a.title}
        </h1>
        <p className="mt-8 font-serif italic text-xl md:text-2xl text-sand-700 leading-snug">
          {a.excerpt}
        </p>
        <div className="mt-8 pb-8 border-b border-sand-200 flex flex-wrap items-center gap-4 text-xs font-mono uppercase tracking-widest text-sand-600">
          <span>By {a.author}</span>
          <span>·</span>
          <span className="flex items-center gap-1.5"><Clock size={11} /> {a.read_time}</span>
        </div>
      </article>

      <div className="max-w-6xl mx-auto px-6 md:px-12">
        <div className="img-zoom aspect-[16/9]">
          <img src={a.cover_image} alt={a.title} className="w-full h-full object-cover" />
        </div>
      </div>

      <article className="max-w-3xl mx-auto px-6 md:px-12 py-20">
        {a.body.split("\n\n").map((p, i) => (
          <p key={i} className={`font-serif text-lg md:text-xl leading-[1.7] text-sand-900 mb-6 ${i === 0 ? "drop-cap text-xl md:text-2xl" : ""}`}>
            {p}
          </p>
        ))}
      </article>

      {related.length > 0 && (
        <section className="bg-white border-y border-sand-200 py-20">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="font-mono text-[11px] tracking-[0.3em] uppercase text-brand-500 mb-8">
              Keep Reading
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
              {related.map((r, i) => <ArticleCard key={r.id} a={r} variant="feature" index={i} />)}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
